import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  Container,
  Box,
  Paper,
  CircularProgress,
} from '@mui/material';
import useCoreApi from '../Utils/useCoreApi';

const AssessmentResult = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    suggestedYearLevel,
    score,
    total,
    answers = [],
    bandPerformance = [],
  } = location.state || {};

  const [aiFeedback, setAiFeedback] = useState('');
  const [loadingFeedback, setLoadingFeedback] = useState(true);

  const handleRetake = () => {
    navigate('/assessments');
  };

  const handleContinue = () => {
    navigate('/LessonSelect');
  };

  const coreApi = useCoreApi();

  useEffect(() => {
    let didRun = false;
  
    const fetchAIFeedback = async () => {
      if (didRun) return;
      didRun = true;
  
      if (!suggestedYearLevel || !answers || answers.length === 0) {
        setAiFeedback("Sorry, I couldn't analyse your answers.");
        setLoadingFeedback(false);
        return;
      }
  
      try {
        const res = await coreApi.post('/assessment/analyse', {
          suggestedYearLevel,
          answers,
          score,
          total,
          bandPerformance: bandPerformance || [],
        });
        setAiFeedback(res.data.feedback || 'No feedback available.');
      } catch (error) {
        console.error('AI Tutor error:', error);
        setAiFeedback('There was a problem getting tutor feedback.');
      } finally {
        setLoadingFeedback(false);
      }
    };
  
    fetchAIFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 👈 only runs once
  
  

  if (!suggestedYearLevel) {
    return (
      <Container maxWidth="sm">
        <Box textAlign="center" mt={8}>
          <Typography variant="h6" gutterBottom>
            Sorry, we couldn't retrieve your assessment result.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRetake}
            sx={{ mt: 2 }}
          >
            Take Assessment Again
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box textAlign="center" mt={8} mb={4}>
        <Typography variant="h4" gutterBottom>
          🎯 Assessment Complete
        </Typography>

        <Typography variant="h6" gutterBottom>
          Your suggested starting point:{' '}
          <strong>Year {suggestedYearLevel}</strong>
        </Typography>

        {score !== undefined && total !== undefined && (
          <Typography variant="body1" color="textSecondary" gutterBottom>
            You answered {score} out of {total} questions correctly.
          </Typography>
        )}

        <Typography variant="body1" sx={{ mt: 2 }}>
          This recommendation helps us ensure you begin at the right level for
          success. You can always move up or down later.
        </Typography>
      </Box>

      {/* CRT-Style AI Feedback */}
      <Box mt={6}>
        <Typography variant="h6" gutterBottom>
          🤖 AI Tutor Feedback
        </Typography>

        <Paper
          elevation={3}
          sx={{
            p: 2,
            backgroundColor: '#000',
            color: '#00FF00',
            fontFamily: 'Courier New, monospace',
            border: '2px solid #00FF00',
            borderRadius: '8px',
            boxShadow: '0 0 8px #00FF00',
            minHeight: '150px',
            whiteSpace: 'pre-wrap',
          }}
        >
          {loadingFeedback ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress size={24} sx={{ color: '#00FF00', mb: 2 }} />
              <Typography sx={{ color: '#00FF00' }}>
                AI Tutor is analysing your results...
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ color: '#00FF00' }}>{aiFeedback}</Typography>
          )}
        </Paper>
      </Box>

      {/* Answer Breakdown */}
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          📋 Your Answers
        </Typography>
        {answers && answers.length > 0 ? (
          answers.map((a, idx) => (
            <Box
              key={idx}
              mb={2}
              p={2}
              border="1px solid #ccc"
              borderRadius={2}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Q{idx + 1}: {a.question_text}
              </Typography>
              <Typography>
                Your answer: <strong>{a.selected_answer}</strong>{' '}
                {a.is_correct ? '✅' : '❌'}
              </Typography>
              {!a.is_correct && (
                <Typography color="textSecondary">
                  Correct answer: <strong>{a.correct_answer}</strong>
                </Typography>
              )}
              <Typography variant="caption" color="textSecondary">
                Year Level: {a.year_level}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography>No answer details available.</Typography>
        )}
      </Box>

      {/* Navigation Buttons */}
      <Box textAlign="center" mt={6} mb={6}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleContinue}
          sx={{ mr: 2 }}
        >
          Continue to Lessons
        </Button>
        <Button variant="text" onClick={handleRetake}>
          Retake Assessment
        </Button>
      </Box>
    </Container>
  );
};

export default AssessmentResult;
